import { useCallback, useEffect, useState } from 'react';
import { isFeatureOn, isTerminal } from '@delivery/shared';
import { ApiFailure, api, download } from '../lib/api';
import { toReceipt } from '../lib/receipt';
import { patchCachedOrder, readCache, writeCache } from '../lib/cached';
import { expectedLabel, money } from '../lib/format';
import {
  STATUS_META,
  type DriverRow,
  type ListResponse,
  type OrderRow,
  type OrderStatus,
  type PickupPointRow,
  type ZoneRow,
} from '../lib/types';
import { AssignDialog } from '../components/AssignDialog';
import { BatchActionDialog } from '../components/BatchActionDialog';
import { ExpectedDateDialog } from '../components/ExpectedDateDialog';
import { dayIn } from '@delivery/shared';
import { PrintDialog } from '../components/PrintDialog';
import { OrderDetail } from '../components/OrderDetail';
import { EMPTY_FILTERS, OrderFilters, type Filters } from '../components/OrderFilters';
import { AutoAssignDialog } from '../components/AutoAssignDialog';
import { ChainImportDialog } from '../components/ChainImportDialog';
import { SpecialItemsDialog } from '../components/SpecialItemsDialog';
import { ImportDialog } from '../components/ImportDialog';
import { CreateOrderDialog, type PrefillOrder } from '../components/CreateOrderDialog';
import { currentRoute, setRoute } from '../lib/route';
import { Printer, Upload, X } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

/**
 * 状态页签 + 筛选条件 → query。
 *
 * 列表和导出共用同一个函数 —— 各拼一份的话,
 * 「界面筛出 12 单、导出得到 300 单」这种事只会在对账时才被发现
 */
function queryParams(
  tab: string,
  f: Filters,
  opts: { withTotal?: boolean; tabs: ReturnType<typeof statusTabs> },
): URLSearchParams {
  const p = new URLSearchParams();
  const statuses = opts.tabs.find((t) => t.key === tab)?.statuses;
  if (statuses) p.set('status', statuses.join(','));
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.driverId) p.set('driverId', f.driverId);
  if (f.zoneId) p.set('zoneId', f.zoneId);
  if (f.pickupPointId) p.set('pickupPointId', f.pickupPointId);
  if (f.needsReview) p.set('needsReview', 'true');
  if (f.codPendingOnly) p.set('codPendingOnly', 'true');
  if (f.unprintedOnly) p.set('receiptPrinted', 'false');
  // date input 给的是本地日期,转成当天起止的 UTC 时刻 ——
  // 直接传 "2026-08-24" 后端按 UTC 零点算,会漏掉当地一整天的单
  if (f.createdFrom) p.set('createdFrom', new Date(`${f.createdFrom}T00:00:00`).toISOString());
  if (f.createdTo) p.set('createdTo', new Date(`${f.createdTo}T23:59:59.999`).toISOString());
  if (opts.withTotal) {
    p.set('limit', '60');
    p.set('withTotal', 'true');
  }
  return p;
}

/**
 * 状态页签。
 *
 * ★ 页签跟着「派单免确认」这个开关变:
 *
 *   开关**关**着时,派单落到 ASSIGNED,「待接单」这一栏是调度每天盯的
 *   —— 谁还没点确认。
 *
 *   开关**开**着时,没有任何一单会停在 ASSIGNED ——
 *   那一栏**永远是空的**,而调度会以为是筛错了。
 *
 * ★ 顺带补上「待取货」(ACCEPTED)。这一栏原来根本没有:
 *   司机接了单还没去取货的那段时间,订单只出现在「进行中」和「全部」里,
 *   而「谁接了还没动」正是调度最想单独看的一批。
 */
function statusTabs(assignWithoutAccept: boolean): Array<{
  key: string;
  label: string;
  statuses?: OrderStatus[];
}> {
  return [
    {
      key: 'active',
      label: '进行中',
      statuses: ['PENDING', 'ASSIGNED', 'ACCEPTED', 'PICKED_UP', 'IN_TRANSIT'],
    },
    { key: 'pending', label: '待派单', statuses: ['PENDING'] },
    ...(assignWithoutAccept
      ? []
      : [{ key: 'assigned', label: '待接单', statuses: ['ASSIGNED'] as OrderStatus[] }]),
    { key: 'accepted', label: '待取货', statuses: ['ACCEPTED'] },
    { key: 'delivering', label: '配送中', statuses: ['PICKED_UP', 'IN_TRANSIT'] },
    { key: 'failed', label: '异常', statuses: ['FAILED'] },
    { key: 'done', label: '已完成', statuses: ['DELIVERED', 'RETURNED'] },
    { key: 'all', label: '全部' },
  ];
}

export function Orders({
  drivers,
  zones,
  points,
  permissions,
  features,
}: {
  drivers: DriverRow[];
  zones: ZoneRow[];
  points: PickupPointRow[];
  permissions: string[];
  /** 租户功能开关的原始值 —— 用 isFeatureOn 判断,它会回落到注册表默认值 */
  features?: Record<string, boolean>;
}) {
  const [tab, setTab] = useState('active');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);
  /*
   * ★ 页签跟着开关变(见 statusTabs 的说明)。
   *   放在这里算而不是模块顶层的常量 —— 开关是租户级的,
   *   而常量在模块加载时就定死了。
   */
  const assignWithoutAccept = isFeatureOn(features, 'assignWithoutAccept');
  const tabs = statusTabs(assignWithoutAccept);

  const [importing, setImporting] = useState(false);
  const [chainImporting, setChainImporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);
  /*
   * ★ 运费这一列按权限出。
   *   `order:view_price` 是单独一个权限点 —— 有人只该看得到「这单送到哪」,
   *   看不到「这单收了多少」。列表接口本来就按它决定要不要下发 customerPrice,
   *   前端跟着同一个判断,不然会画出一列空白。
   */
  const canSeePrice = permissions.includes('order:view_price');
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | undefined>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** 要打印的那几单 —— 贴在包裹上的小票 */
  const [printing, setPrinting] = useState<OrderRow[] | null>(null);
  const [marking, setMarking] = useState(false);
  const [assigning, setAssigning] = useState(false);
  /** 批量设置特殊物品的弹窗开着没有 */
  const [pickingItems, setPickingItems] = useState(false);
  /** 批量改期望送达日期的小弹窗开着没有 */
  const [pickingDate, setPickingDate] = useState(false);
  /**
   * 店里的时区。
   *
   * ★ 「今天」得按**店里**算,不按这台电脑 —— 老板在国外出差打开管理端,
   *   按浏览器时区算出来的「今天」可能是店里的昨天或明天,
   *   而他点的那一下会把一批单改到错误的那一天,司机端第二天全亮红。
   *   拿不到就退回浏览器时区(总比空着强),那种情况只会在接口挂了时出现。
   */
  const [tenantTimezone, setTenantTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  useEffect(() => {
    void api<{ timezone: string }>('/orders/overview')
      .then((r) => r.timezone && setTenantTimezone(r.timezone))
      .catch(() => undefined);
  }, []);
  /** 批量改状态(取货 / 送达 / 未送达 / 取消 …)—— 见 BatchActionDialog */
  const [batching, setBatching] = useState(false);
  /** 「再来一单」带给建单弹窗的资料 —— 建完或关掉就清空 */
  const [prefill, setPrefill] = useState<PrefillOrder | null>(null);
  /*
   * ★ 打开的是哪一单也写进地址栏(`#/orders/<单号 id>`)。
   *
   *   用户报的:「订单详情页刷新后就不在这个页面了」。
   *   调度看一单常常要刷一下(等司机的动作落下来),
   *   每次都被扔回列表、再翻回那一行 —— 而列表可能已经滚了很远。
   *
   *   顺带白拿一样:这个地址可以直接发给同事,他打开就是同一单。
   */
  const [detailId, setDetailIdRaw] = useState<string | null>(() => currentRoute()[1] ?? null);
  const setDetailId = useCallback((id: string | null) => {
    setDetailIdRaw(id);
    setRoute(id ? ['orders', id] : ['orders']);
  }, []);

  /* 浏览器后退 —— 地址回到列表时详情也要跟着关 */
  useEffect(() => {
    const onHash = () => setDetailIdRaw(currentRoute()[1] ?? null);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 眼前这屏是上次缓存的,新的还在路上 */
  const [stale, setStale] = useState(false);

  const load = useCallback(
    async (append = false) => {
      setLoading(true);
      const params = queryParams(tab, filters, { withTotal: true, tabs });
      if (append && cursor) params.set('cursor', cursor);
      const path = `/orders?${params}`;

      /*
       * ★ 先把上一次这组筛选的结果摆出来,再去要新的。
       *
       *   用户原话:「每次打开新的页面都要重新加载数据然后有了数据后才显示」。
       *   调度员一天里在概览、订单、地图之间切几十次,每次都对着空屏等一下。
       *
       *   只做第一页 —— 翻页(append)本来就是人主动要的,他在等,
       *   而且缓存一页以后的内容会和新数据接不上。
       *
       *   拿旧数据去操作不会出事:状态变更都带乐观锁,订单被别人改过
       *   会被挡回来(「请刷新后重试」),不会静默覆盖。
       */
      if (!append) {
        const cached = await readCache<ListResponse>(path);
        if (cached) {
          setRows(cached.data);
          setTotal(cached.meta.total);
          setStale(true);
        }
      }

      try {
        const r = await api<ListResponse>(path);
        setRows((prev) => (append ? [...prev, ...r.data] : r.data));
        setCursor(r.meta.cursor.next);
        setHasMore(r.meta.cursor.hasMore);
        setTotal(r.meta.total);
        setError(null);
        setStale(false);
        if (!append) void writeCache(path, r);
      } catch (e) {
        // ★ 手上有缓存时不清屏 —— 网络抖一下不该把满屏数据换成一句红字
        setError(e instanceof ApiFailure ? e.message : '加载失败');
      } finally {
        setLoading(false);
      }
    },
    [tab, filters, cursor],
  );

  /**
   * 就地改几单 —— 界面**立刻**变。
   *
   * ★ 用户报的:「管理端操作订单后,订单列表没有变化,应该和司机端一样,
   *   可以本地缓存下状态,然后修改数据后至少本地先改下状态」。
   *
   * ★ 在这之前每个动作后面跟的是一次整表重拉:
   *   ① 重拉先读缓存,而缓存里那条还是**改之前**的状态 —— 于是他眼看着
   *      自己刚点完的那一单还写着老状态,以为没生效,又点一次;
   *   ② 重拉只拿第一页,翻过好几页的话后面那些行直接没了,
   *      人被甩回列表顶上(用户也提了「希望刷新后还滚到当前位置」)。
   *
   *   所以改成:先就地改(眼前这一刻是对的),再**悄悄**去核对一遍。
   *
   * ★ 改完的那一行**不从列表里拿掉**,哪怕它已经不符合当前页签
   *   (在「进行中」里标了送达)。他要看的正是「这单变成已送达了」——
   *   让它当场消失,和「没反应」一样让人怀疑自己有没有点中。
   *   下次切页签 / 改筛选时自然就不在了。
   */
  const patchRows = useCallback((ids: string[], patch: Partial<OrderRow>) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    setRows((prev) => prev.map((r) => (idSet.has(r.id) ? { ...r, ...patch } : r)));
    // 缓存里那份也要改 —— 不改的话下次进这一屏,旧状态又从缓存里回来了
    for (const id of ids) {
      void patchCachedOrder(
        id,
        patch as Record<string, unknown>,
        patch.status ? isTerminal(patch.status) : false,
      );
    }
  }, []);

  /**
   * 悄悄去服务端核对一遍,**不动滚动位置**。
   *
   * ★ 只更新已经在屏幕上的那些行(按 id 合),不截断、不插入、不清屏 ——
   *   列表的高度和行数一个都不变,所以人停在哪儿还是哪儿。
   *   这是「刷新后还滚到当前位置」最简单可靠的做法:根本不让它跳。
   *
   * ★ 失败就算了:眼前这份已经被就地改过,是对的;
   *   而为一次后台核对弹一句红字,只会让人以为操作没成功。
   */
  const revalidate = useCallback(async () => {
    const params = queryParams(tab, filters, { withTotal: true, tabs });
    const path = `/orders?${params}`;
    try {
      const r = await api<ListResponse>(path);
      const fresh = new Map(r.data.map((o) => [o.id, o]));
      setRows((prev) => prev.map((row) => fresh.get(row.id) ?? row));
      setTotal(r.meta.total);
      void writeCache(path, r);
    } catch {
      /* 后台核对失败不打扰人 —— 见上面 */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters]);

  /** 动过之后:先就地改,再悄悄核对 */
  const afterChange = useCallback(
    (ids: string[], patch: Partial<OrderRow>) => {
      patchRows(ids, patch);
      void revalidate();
    },
    [patchRows, revalidate],
  );

  useEffect(() => {
    setCursor(null);
    setSelected(new Set());
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // 搜索防抖
  useEffect(() => {
    const t = setTimeout(() => {
      setCursor(null);
      void load(false);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function doExport() {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      await download(`/orders/export?${queryParams(tab, filters, { tabs })}`);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '导出失败');
    } finally {
      setExporting(false);
    }
  }

  /*
   * ★ 已交给三方的单不算「可派」。
   *   后端会拒(guards.notHandedToChannel),但按钮上的数字要说实话 ——
   *   否则点完才发现「派了 5 单成功 3 单」,而那两单为什么失败要点开才知道。
   */
  /**
   * 按当前计价规则重算运费。
   *
   * ★ 结果**逐单**摊开,不是一句「重算了 12 单」。
   *   跳过的那几单为什么跳过(已进结算、没有匹配规则、算出来一样),
   *   不说清楚的话人只能去挨个点开看。
   */
  /*
   * 批量重排地理编码。
   *
   * ★ 只是把它们重新排队 —— 后台的 worker 去编码。
   *   所以按钮说的是「已排入 N 单」而不是「已完成 N 单」:
   *   前者是真的,后者会让人以为刷新就能看到结果。
   */
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoResult, setGeoResult] = useState<number | null>(null);

  async function retryGeocode() {
    setGeoBusy(true);
    setError(null);
    try {
      const r = await api<{ queued: number }>('/orders/geocode-retry', {
        method: 'POST',
        body: { limit: 200 },
      });
      setGeoResult(r.queued);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '重新编码失败');
    } finally {
      setGeoBusy(false);
    }
  }

  const [repricing, setRepricing] = useState(false);
  const [repriceResult, setRepriceResult] = useState<{
    summary: { repriced: number; skipped: number };
    results: Array<{ orderNo: string; status: string; reason?: string }>;
  } | null>(null);

  async function reprice() {
    setRepricing(true);
    setError(null);
    try {
      const r = await api<{
        summary: { repriced: number; skipped: number };
        results: Array<{ orderNo: string; status: string; reason?: string }>;
      }>('/orders/reprice', { method: 'POST', body: { orderIds: [...selected] } });
      setRepriceResult(r);
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '重算失败');
    } finally {
      setRepricing(false);
    }
  }

  /** 选中的是不是**全都**打过了 —— 决定那颗按钮该往哪个方向 */
  const allPickedPrinted =
    selected.size > 0 &&
    rows.filter((r) => selected.has(r.id)).every((r) => r.receiptPrintedAt != null);

  /**
   * 手工标记打印状态。
   *
   * ★ 两个方向都要有:在别处打过了就标上(别再出现在「没打过」里),
   *   标错了或者要重打一批就退回去 —— 只给单向的话,
   *   手滑标错的那一批再也印不出来,而那一批的纸还没贴上去。
   *
   * ★ 标完**不清空选择**:人多半要接着对这一批做别的事(比如标完就派单)。
   */
  async function markPrinted(printed: boolean) {
    const picked = rows.filter((r) => selected.has(r.id));
    if (picked.length === 0) return;
    setMarking(true);
    try {
      await api('/print-jobs/mark-printed', {
        method: 'POST',
        body: { orderNos: picked.map((o) => o.orderNo), printed },
      });
      // 🖨 那个标记要当场出现 / 消失 —— 人接着就要按「只看没打过的」筛
      afterChange(
        picked.map((o) => o.id),
        { receiptPrintedAt: printed ? new Date().toISOString() : null },
      );
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '标记失败');
    } finally {
      setMarking(false);
    }
  }

  const assignable = rows.filter(
    (r) =>
      selected.has(r.id) &&
      ['PENDING', 'ASSIGNED'].includes(r.status) &&
      r.dispatchMode !== 'THIRD_PARTY',
  );
  /*
   * 按区域派只动待派的单。
   * 已派单的走「派发」手工改派 —— 批量按区域派把人工特意指定的司机
   * 悄悄换掉,是没人能预料到的破坏(后端也同样拦着,这里只是别让按钮说谎)
   */
  const pendingSelected = rows.filter(
    (r) => selected.has(r.id) && r.status === 'PENDING' && r.dispatchMode !== 'THIRD_PARTY',
  );

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">
          订单
        </h1>
        <div className="flex gap-2">
          {permissions.includes('import:create') && (
            <button
              onClick={() => setImporting(true)}
              className="h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600
                         text-slate-700 dark:text-slate-300 text-sm font-medium"
            >
              <InlineIcon icon={Upload} /> 导入订单
            </button>
          )}
          {/*
            ★ 接龙导入单独一个入口,不做成「导入订单」里的一个模式。
              两者的表不是一个东西:那边是商家发来的规整模板(可以选列映射),
              这边是接龙小程序导出的原表 —— 标题占第一行、一行一个商品、
              地址是客户自己在微信里填的。挤在一个对话框里的话,
              两套完全不同的字段会打在一起。
          */}
          {permissions.includes('import:create') && (
            <button
              onClick={() => setChainImporting(true)}
              className="h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600
                         text-slate-700 dark:text-slate-300 text-sm font-medium"
            >
              <InlineIcon icon={Upload} /> 接龙导入
            </button>
          )}
          {/*
            ★ 单张建单排在导入右边、用主按钮色。
              导入是「一天开始时把商家发来的表灌进来」,
              建单是「电话响了」—— 后者一天里发生几十次。
          */}
          {permissions.includes('order:create') && (
            <button
              onClick={() => setCreating(true)}
              className="h-10 px-4 rounded-lg bg-brand-600 text-white text-sm font-medium"
            >
              + 新建订单
            </button>
          )}
        </div>
      </div>

      <OrderFilters
        value={filters}
        onChange={setFilters}
        zones={zones}
        drivers={drivers}
        points={points}
        canExport={permissions.includes('order:export')}
        exporting={exporting}
        onExport={() => void doExport()}
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition ${
              tab === t.key
                ? 'border-brand-600 text-brand-600 dark:text-brand-500 font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/*
        ★ 显示缓存就必须说出来。
          不说的话,调度员看着一屏三分钟前的单去派活 ——
          而他没有任何理由怀疑眼前这份不是最新的。
          (真去操作会被乐观锁挡住,但那时他已经按错的信息做了决定。)
      */}
      {stale && (
        <div
          aria-live="polite"
          aria-label="正在更新"
          className="mb-3 rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-600
                     dark:bg-slate-800 dark:text-slate-300"
        >
          这是上次打开时的数据,正在取最新的…
        </div>
      )}

      {error && (
        <div
          className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-2 text-red-700 dark:text-red-300 text-sm"
        >
          {error}
        </div>
      )}

      {/*
        批量操作条:选中后才出现,不占常驻空间。

        ★ 窄屏上要换行。不换的话四五颗按钮在 390px 上挤出屏幕右边 ——
          而挤出去的那几颗**看不见也点不到**,人只会以为手机上没有批量功能。
      */}
      {selected.size > 0 && (
        <div
          /*
           * ★ 粘在顶上 —— 不管列表滑到哪儿,勾了就在眼前。
           *
           *   `--mobile-bar-h` 是手机顶栏的高度(App 里含刘海那一截),桌面没有那条栏
           *   所以 `lg:top-0`。粘错高度的话它会压在顶栏上,或者露出一条会闪的缝。
           *
           * ★ 用 sticky 而不是 fixed:sticky **占着自己那份布局空间**,
           *   所以滑回顶部时后面的数据自然排在它下面,不会被挡;
           *   fixed 脱离文档流,顶部第一行会被永久盖住。
           *   用户原话:「把数据拉到顶部的时候数据要在这个区域下面不要被挡住」。
           *
           * ★ 背景必须是实色。半透明的话,滑动时下面的表格会从它身后透出来,
           *   看上去像重影。
           */
          className="sticky top-[var(--mobile-bar-h)] z-10 mb-3 flex flex-wrap items-center justify-between gap-2
                     rounded-lg border border-brand-200 bg-brand-50 px-4 py-2.5 shadow-sm
                     lg:top-0 dark:border-brand-800 dark:bg-brand-950"
        >
          <span className="text-sm text-slate-700 dark:text-slate-300">
            已选 {selected.size} 单
            {assignable.length !== selected.size && (
              <span className="text-amber-600 dark:text-amber-400 ml-2">
                (其中 {assignable.length} 单可派)
              </span>
            )}
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400"
            >
              取消选择
            </button>
            {/*
              ★ 批量改状态。用户原话:「不止可以批量取消,可以批量标记取货,
                送达/未送达等所有操作,批量转派司机」。
                能做哪些动作由状态机表算,见 BatchActionDialog。
            */}
            <button
              onClick={() => setBatching(true)}
              className="px-4 py-1.5 rounded-lg border border-slate-400 text-slate-700
                         dark:text-slate-300 text-sm"
            >
              批量操作
            </button>
            {/*
              ★ 打印不看状态:一叠单子什么时候贴标签是仓库自己的节奏,
                有人建完就贴,有人等派完司机再按司机分堆打。
                拦着「只有已派单才能打」只会让人去别处想办法。
            */}
            <button
              onClick={() => setPrinting(rows.filter((r) => selected.has(r.id)))}
              className="px-4 py-1.5 rounded-lg border border-slate-400 text-slate-700
                         dark:text-slate-300 text-sm"
            >
              打印小票 {selected.size} 张
            </button>
            {/*
              ★ 手工标记打印状态。两个方向都要有:
                · 在别处打过了(换了台机器、手写了一张)→ 标上,别再出现在「没打过」里
                · 标错了、或者要重打一批 → 退回去,否则那一批再也印不出来
                按钮的方向跟着选中的单走 —— 全都打过了就只可能是想退回去。
            */}
            {permissions.includes('order:update') && (
              <button
                onClick={() => void markPrinted(!allPickedPrinted)}
                disabled={marking}
                className="px-4 py-1.5 rounded-lg border border-slate-400 text-slate-700
                           dark:text-slate-300 text-sm disabled:opacity-40"
              >
                {marking
                  ? '处理中…'
                  : allPickedPrinted
                    ? `标记未打印 ${selected.size} 单`
                    : `标记已打印 ${selected.size} 单`}
              </button>
            )}
            {/*
              ★ 批量改期望送达日期。
                用户原话:「批量操作里面可以批量修改一些订单的预期达到时间」。

                一车货临时改成明天送,是挨个点进去改二十次,还是勾一批改一次 ——
                前者他多半就不改了,于是司机端第二天早上亮二十条「没送完」。
            */}
            {permissions.includes('order:update') && (
              <button
                onClick={() => setPickingDate(true)}
                className="px-4 py-1.5 rounded-lg border border-slate-400 text-slate-700
                           dark:text-slate-300 text-sm"
              >
                改送达日期 {selected.size} 单
              </button>
            )}
            {/*
              ★ 批量设置特殊物品。
                用户原话:「批量操作里面增加一个设置冻品的功能,就是设置特殊物品的
                功能,可以批量设置特殊物品」。

                一整批团购货全是冻品时,一张张点开去勾是几十次点击 ——
                而不勾的后果是司机不知道要带保温箱,货到就化了。
            */}
            {permissions.includes('order:update') && (
              <button
                onClick={() => setPickingItems(true)}
                className="px-4 py-1.5 rounded-lg border border-slate-400 text-slate-700
                           dark:text-slate-300 text-sm"
              >
                设置特殊物品 {selected.size} 单
              </button>
            )}
            {/*
              ★ 按区域派单是「一次分完一天的单」的入口,
                和「派发」并列而不是藏在菜单里 —— 它才是日常操作,
                手工指定司机是例外情况
            */}
            <button
              onClick={() => setAutoAssigning(true)}
              disabled={pendingSelected.length === 0}
              className="px-4 py-1.5 rounded-lg border border-brand-600 text-brand-600
                         dark:text-brand-500 text-sm font-medium disabled:opacity-40"
            >
              按区域派 {pendingSelected.length} 单
            </button>
            {/*
              ★ 只有能重算的角色才看得到这个按钮(财务)。
                改一单的价就是改客户账单和司机收入两件事,
                调度员不该顺手做 —— 后端也拦着,这里是第一道。
            */}
            {permissions.includes('order:reprice') && (
              <button
                onClick={() => void reprice()}
                disabled={repricing}
                className="px-4 py-1.5 rounded-lg border border-slate-400 text-slate-700
                           dark:text-slate-300 text-sm disabled:opacity-40"
              >
                {repricing ? '重算中…' : `重算运费 ${selected.size} 单`}
              </button>
            )}
            <button
              onClick={() => setAssigning(true)}
              disabled={assignable.length === 0}
              className="px-4 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium
                         disabled:opacity-40"
            >
              派发 {assignable.length} 单
            </button>
          </div>
        </div>
      )}

      {/*
        ★ 「重新编码」只在**筛着待复核**的时候出现。
          它是一个针对性动作(把队列里缺坐标的单重新排一次地理编码),
          平时挂在那儿只会让人误点 —— 而地理编码是按次收费的。
      */}
      {filters.needsReview && permissions.includes('order:update') && (
        <div
          className="mb-3 flex items-center justify-between rounded-lg border
                        border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm"
        >
          <span className="text-slate-600 dark:text-slate-400">
            {geoResult
              ? `已排入 ${geoResult} 单,后台正在处理 —— 过一会儿刷新看看`
              : '这些单缺坐标,上不了地图、司机也导航不了'}
          </span>
          <button
            onClick={() => void retryGeocode()}
            disabled={geoBusy}
            className="shrink-0 rounded-lg border border-slate-400 px-3 py-1.5
                       text-slate-700 dark:text-slate-300 disabled:opacity-40"
          >
            {geoBusy ? '排队中…' : '重新编码'}
          </button>
        </div>
      )}

      {repriceResult && (
        <div
          className="mb-3 rounded-lg border border-slate-200 dark:border-slate-700
                        bg-white dark:bg-slate-800 px-4 py-3 text-sm"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-800 dark:text-slate-200">
              重算了 {repriceResult.summary.repriced} 单
              {repriceResult.summary.skipped > 0 && ` · 跳过 ${repriceResult.summary.skipped} 单`}
            </span>
            <button
              onClick={() => setRepriceResult(null)}
              className="text-slate-400 hover:text-slate-600"
              aria-label="关闭重算结果"
            >
              <InlineIcon icon={X} />
            </button>
          </div>
          {repriceResult.results.filter((r) => r.status === 'SKIPPED').length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
              {repriceResult.results
                .filter((r) => r.status === 'SKIPPED')
                .map((r) => (
                  <li key={r.orderNo}>
                    <span className="font-mono">{r.orderNo}</span> —— {r.reason}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {/*
        ★ 手机上换成卡片列表,不是把表格横着滚。
    
          九列的表格在 390px 宽的屏幕上,横滚要划四五次才看得到司机是谁 ——
          而调度在路上要的就是「这单谁在送、到哪了」。
          横滚还会藏起最右边的列,人根本不知道右边还有东西。
      */}
      {/*
        ★ 手机上也要能全选 —— 表格那个「表头勾选框」在这一屏根本不存在,
          而「选中二十单一起打印」正是在手机上最想做的事。
      */}
      {rows.length > 0 && (
        <div className="mb-2 flex items-center gap-3 sm:hidden">
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              aria-label="全选这一页"
              className="h-5 w-5"
              checked={selected.size === rows.length}
              onChange={(e) =>
                setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
              }
            />
            全选这一页
          </label>
        </div>
      )}

      <ul aria-label="订单卡片" className="space-y-2 sm:hidden">
        {rows.map((r) => {
          const meta = STATUS_META[r.status];
          return (
            <li key={r.id} className="flex items-stretch gap-1">
              {/*
                ★ 勾选框和卡片是**两个**点击区域:卡片点开详情,勾选框只勾选。
                  合成一个的话,想勾一单的人会掉进详情页,而想看详情的人
                  会莫名其妙勾中一堆。
                ★ 勾选那块给足宽度(整条高、44px 宽)—— 手指不是鼠标指针。
              */}
              <label
                className="flex w-11 shrink-0 items-center justify-center rounded-xl border
                           border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
              >
                <input
                  type="checkbox"
                  aria-label={`选择 ${r.orderNo}`}
                  className="h-5 w-5"
                  checked={selected.has(r.id)}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(r.id);
                    else next.delete(r.id);
                    setSelected(next);
                  }}
                />
              </label>
              <button
                onClick={() => setDetailId(r.id)}
                className={`min-w-0 flex-1 rounded-xl border bg-white p-3 text-left
                           dark:bg-slate-800 ${
                             selected.has(r.id)
                               ? 'border-brand-400 dark:border-brand-600'
                               : 'border-slate-200 dark:border-slate-700'
                           }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-brand-600 dark:text-brand-500">
                    {r.orderNo}
                    {r.needsReview && <span className="ml-1 text-amber-500">⚠</span>}
                  </span>
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="mt-1 text-slate-900 dark:text-slate-100">
                  {r.receiverName}
                  <span className="ml-2 text-xs text-slate-400">{r.receiverPhone}</span>
                  {/*
                    ★ 期望送达日期在手机上也得露出来。
                      电脑那一屏它有自己一列,而手机是卡片 —— 这一列原来
                      整个不存在,于是调度员在手机上看到的每一单都像是今天的,
                      「明天送」这件事只有回到电脑前才看得见。

                    ★ 跟在电话后面,不另起一行:卡片已经有姓名/地址/区域/
                      司机/运费五行,再加一行会把「一屏几单」直接砍掉。
                  */}
                  {r.expectedDeliveryAt && (
                    <span className="ml-2 text-xs">
                      <ExpectedDay at={r.expectedDeliveryAt} tz={tenantTimezone} status={r.status} />
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {r.receiverAddress}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-400">
                  <span>{r.zone?.name ?? '未匹配区域'}</span>
                  <span>
                    {r.dispatchMode === 'THIRD_PARTY' ? '第三方' : (r.driver?.name ?? '未派单')}
                  </span>
                  <span>{r.packageCount} 件</span>
                  {/*
                    ★ 路线的「第几站 · 约几点到」手机卡片上也要有 ——
                      电脑/iPad 那一屏在收件人下面露着,手机卡片原来没有,
                      调度在手机上被问「他几点到」就答不上来。
                  */}
                  {r.routeSeq != null && (
                    <span className="text-brand-600 dark:text-brand-500">
                      第 {r.routeSeq} 站
                      {r.routeEtaAt && (
                        <span title="含每单停留时间">
                          {' · 约 '}
                          {new Date(r.routeEtaAt).toLocaleTimeString('zh-CN', {
                            timeZone: tenantTimezone,
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })}
                        </span>
                      )}
                    </span>
                  )}
                  {/* ★ 打过的标出来 —— 批量选的时候一眼看得见哪些是重复的 */}
                  {r.receiptPrintedAt && <span className="text-slate-400"><InlineIcon icon={Printer} /> 打过</span>}
                  {/*
                    ★ 手机卡片上运费和「代收」挨着放:两个都是钱,
                      但一个是我们收客户的、一个是司机要向收件人收现金的 ——
                      分开显示金额,不合并成一个数
                  */}
                  {canSeePrice && (
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      {money(r.customerPrice ?? '0')}
                    </span>
                  )}
                  {Number(r.codAmount) > 0 && (
                    <span className="font-medium text-red-600 dark:text-red-400">
                      代收 {money(r.codAmount)}
                    </span>
                  )}
                  {(r.specialItems ?? []).map((i) => (
                    <span key={i.code} className="font-medium text-amber-700 dark:text-amber-400">
                      {i.icon ? `${i.icon} ` : ''}
                      {i.label}
                    </span>
                  ))}
                </div>
              </button>
            </li>
          );
        })}
        {rows.length === 0 && !loading && (
          <li
            className="rounded-xl border border-slate-200 p-8 text-center text-slate-400
                         dark:border-slate-700"
          >
            没有符合条件的订单
          </li>
        )}
      </ul>

      <div
        className="hidden rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                      bg-white dark:bg-slate-800 sm:block"
      >
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
            <tr>
              <th className="w-10 p-3">
                <input
                  type="checkbox"
                  aria-label="全选本页"
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                  }
                />
              </th>
              <Th>单号</Th>
              <Th>状态</Th>
              <Th>收件人</Th>
              <Th>地址</Th>
              <Th>区域</Th>
              {canSeePrice && <Th>运费</Th>}
              <Th>司机</Th>
              <Th>件数</Th>
              {/*
                ★ 期望送达日期要**看得见**。
                  改了却没地方显示,人只能靠「司机端还看不看得到」来反推 ——
                  而那条路太长,中间任何一环出问题都长得像「改了没生效」。
                  (用户报的就是这个:改完看不出变化,以为功能坏了。)
              */}
              <Th>期望送达</Th>
              <Th>创建</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const meta = STATUS_META[r.status];
              return (
                <tr
                  key={r.id}
                  // 整行可点是调度员的肌肉记忆,但键盘用户得有个真的按钮 ——
                  // 所以单号那格是 <button>,整行只是给鼠标的便利
                  onClick={() => setDetailId(r.id)}
                  className="border-t border-slate-100 dark:border-slate-700/50 cursor-pointer
                             hover:bg-slate-50 dark:hover:bg-slate-700/30"
                >
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`选择 ${r.orderNo}`}
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  </td>
                  <td className="p-3 font-mono text-xs">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailId(r.id);
                      }}
                      className="text-brand-600 dark:text-brand-500 hover:underline"
                    >
                      {r.orderNo}
                    </button>
                    {r.needsReview && (
                      <span title="缺少坐标或区域存疑" className="ml-1 text-amber-500">
                        ⚠
                      </span>
                    )}
                    {/* ★ 打过的标出来 —— 批量选的时候一眼看得见哪些是重复的 */}
                    {r.receiptPrintedAt && (
                      <span title="小票已经打过了" className="ml-1 text-slate-400">
                        <InlineIcon icon={Printer} />
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}
                    >
                      {meta.label}
                    </span>
                    {r.subStatus && (
                      <div className="text-xs text-slate-400 mt-0.5">{r.subStatus}</div>
                    )}
                  </td>
                  <td className="p-3 text-slate-900 dark:text-slate-100">
                    {r.receiverName}
                    <div className="text-xs text-slate-400">{r.receiverPhone}</div>
                  </td>
                  {/*
                    ★ 地址列收窄,把宽度让给运费。
                      地址截断了还能点进详情看全,而运费落在横滚区外
                      等于「显示了」但没人看得到。
                  */}
                  <td
                    className="max-w-[200px] truncate p-3 text-slate-600 dark:text-slate-400"
                    title={r.receiverAddress}
                  >
                    {r.receiverAddress}
                  </td>
                  <td className="p-3 text-slate-600 dark:text-slate-400">{r.zone?.name ?? '—'}</td>
                  {canSeePrice && (
                    <td className="p-3 text-right tabular-nums whitespace-nowrap">
                      {/*
                        ★ 右对齐 + 等宽数字。金额一列左对齐的话,
                          12.00 和 8.50 的小数点错开,一眼看不出哪个大。
                      */}
                      <span className="text-slate-900 dark:text-slate-100">
                        {money(r.customerPrice ?? '0')}
                      </span>
                      {Number(r.codAmount) > 0 && (
                        // ★ 代收要标出来:那是司机要向收件人**收现金**的钱,
                        //   和运费不是一回事,混着看会把账对错
                        <span className="block text-xs font-medium text-red-600 dark:text-red-400">
                          代收 {money(r.codAmount)}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="p-3 text-slate-600 dark:text-slate-400">
                    {r.dispatchMode === 'THIRD_PARTY' ? (
                      // ★ 交给三方的单在这一栏说清楚,别让人以为「未派单」
                      <span className="text-slate-700 dark:text-slate-300">
                        第三方
                        {r.channelOrderNo && (
                          <span className="block text-xs text-slate-400 font-mono">
                            {r.channelOrderNo}
                          </span>
                        )}
                      </span>
                    ) : (
                      (r.driver?.name ?? <span className="text-slate-400">未派单</span>)
                    )}
                  </td>
                  <td className="p-3 tabular-nums text-slate-600 dark:text-slate-400">
                    {r.packageCount}
                  </td>
                  <td className="p-3 text-xs whitespace-nowrap">
                    {r.expectedDeliveryAt ? (
                      <ExpectedDay at={r.expectedDeliveryAt} tz={tenantTimezone} status={r.status} />
                    ) : (
                      <span className="text-slate-300 dark:text-slate-600">—</span>
                    )}
                    {/*
                      ★ 司机规划过路线的话,把「第几站 · 预计几点到」显示出来。
                        这是客服被问「他什么时候到」时唯一能看的地方 ——
                        不显示的话她只能打电话问正在开车的司机。

                      ★ 写明「含停留」:这个数不是纯行车时间,
                        而调度会照着它回客户电话。
                    */}
                    {r.routeSeq != null && (
                      <span className="mt-0.5 block text-brand-600 dark:text-brand-500">
                        第 {r.routeSeq} 站
                        {r.routeEtaAt && (
                          <span title="含每单停留时间">
                            {' · 约 '}
                            {new Date(r.routeEtaAt).toLocaleTimeString('zh-CN', {
                              timeZone: tenantTimezone,
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            })}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-400 whitespace-nowrap">
                    {new Date(r.createdAt).toLocaleString('zh-CN', {
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={canSeePrice ? 10 : 9} className="p-10 text-center text-slate-400">
                  没有符合条件的订单
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
        <span>
          显示 {rows.length} 条{total != null && ` / 共 ${total} 条`}
        </span>
        {hasMore && (
          <button
            onClick={() => void load(true)}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300 disabled:opacity-50"
          >
            {loading ? '加载中…' : '加载更多'}
          </button>
        )}
      </div>

      {autoAssigning && (
        <AutoAssignDialog
          orderIds={pendingSelected.map((r) => r.id)}
          onClose={() => setAutoAssigning(false)}
          onDone={async () => {
            setSelected(new Set());
            await revalidate();
          }}
        />
      )}

      {pickingItems && (
        <SpecialItemsDialog
          orders={rows.filter((r) => selected.has(r.id))}
          onClose={() => setPickingItems(false)}
          onDone={async () => {
            setPickingItems(false);
            setSelected(new Set());
            await load(false);
          }}
        />
      )}

      {chainImporting && (
        <ChainImportDialog
          points={points}
          onClose={() => setChainImporting(false)}
          onDone={async () => {
            setCursor(null);
            await load(false);
          }}
        />
      )}

      {importing && (
        <ImportDialog
          permissions={permissions}
          points={points}
          onClose={() => setImporting(false)}
          onDone={async () => {
            setCursor(null);
            await load(false);
          }}
        />
      )}

      {detailId && (
        <OrderDetail
          timezone={tenantTimezone}
          id={detailId}
          drivers={drivers}
          zones={zones}
          permissions={permissions}
          features={features}
          /*
           * ★ 详情里每读到一次新数据,列表就**就地**跟着改。
           *   不用等他关抽屉:在详情里点完送达,身后那张列表当场就变了。
           */
          onChanged={(o) =>
            patchRows([o.id], {
              status: o.status,
              subStatus: o.subStatus,
              driver: o.driver,
              receiverPhone: o.receiverPhone,
              receiptPrintedAt: o.receiptPrintedAt,
              codAmount: o.codAmount,
              codPending: o.codPending,
              specialItems: o.specialItems,
              needsReview: o.needsReview,
              version: o.version,
            })
          }
          onClose={() => {
            setDetailId(null);
            // 关掉时再悄悄核对一遍 —— 不清屏、不跳位置(见 revalidate)
            void revalidate();
          }}
          /*
           * ★ 「再来一单」:把这一单的资料交给建单弹窗预填,然后关掉详情。
           *   关掉是必要的 —— 两个弹窗叠着的话,人分不清自己在改哪一单。
           */
          onRepeat={(o) => {
            /*
             * ★ 只带这个人是谁、货从哪发。
             *   件数、代收、备注这些是**上一单的事实**,不是这个人的属性 ——
             *   「客户要求下午再送」跟过去就错了,而「代收 120」跟过去更糟:
             *   司机会照着去收一笔本来不存在的钱。
             */
            setPrefill({
              receiverName: o.receiverName,
              receiverPhone: o.receiverPhone,
              receiverAddress: o.receiverAddress,
              receiverLat: o.receiverLat == null ? null : Number(o.receiverLat),
              receiverLng: o.receiverLng == null ? null : Number(o.receiverLng),
              pickupPointId: o.pickupPoint?.id ?? null,
              customerId: o.customer?.id ?? null,
            });
            setDetailId(null);
            setCreating(true);
          }}
        />
      )}

      {printing && (
        <PrintDialog
          orders={printing.map(toReceipt)}
          /* 🖨 标记当场出现 —— 不用刷新一次才看得到哪几单打过了 */
          onPrinted={() =>
            afterChange(
              printing.map((o) => o.id),
              { receiptPrintedAt: new Date().toISOString() },
            )
          }
          onClose={() => setPrinting(null)}
        />
      )}

      {pickingDate && (
        <ExpectedDateDialog
          orders={rows.filter((r) => selected.has(r.id))}
          timezone={tenantTimezone}
          onClose={() => setPickingDate(false)}
          onDone={async () => {
            setSelected(new Set());
            await revalidate();
          }}
        />
      )}

      {batching && (
        <BatchActionDialog
          orders={rows.filter((r) => selected.has(r.id))}
          drivers={drivers}
          permissions={permissions}
          onClose={() => setBatching(false)}
          /* 成功的那几单当场改;失败的保持原样 —— 结果页会逐条说明为什么 */
          onPatched={(changes) => {
            for (const c of changes) patchRows([c.id], { status: c.status as OrderStatus });
          }}
          onDone={async () => {
            setSelected(new Set());
            await revalidate();
          }}
        />
      )}

      {assigning && (
        <AssignDialog
          features={features}
          orders={assignable}
          drivers={drivers}
          onClose={() => setAssigning(false)}
          onDone={async () => {
            setAssigning(false);
            setSelected(new Set());
            /*
             * 派完之后那几单的司机和状态都变了,而「派给谁」这一列
             * 是调度接着要看的 —— 悄悄核对一遍就够,不用清屏重来。
             */
            await revalidate();
          }}
        />
      )}

      {creating && (
        <CreateOrderDialog
          timezone={tenantTimezone}
          points={points}
          zones={zones}
          features={features}
          prefill={prefill}
          onClose={() => {
            setCreating(false);
            setPrefill(null);
          }}
          onCreated={async () => {
            setCreating(false);
            setPrefill(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/**
 * 订单 → 小票内容。
 *
 * ★ 金额在接口上是**字符串**(分),因为 BigInt 过不了 JSON。
 *   直接丢给小票层的话,`> 0` 对字符串是字典序比较 ——
 *   「0」和「0.00」都为真,于是每张票都印一行「代收现金 $0.00」。
 */

function Th({ children }: { children: React.ReactNode }) {
  return <th className="p-3 text-left font-medium whitespace-nowrap">{children}</th>;
}

/**
 * 期望送达那一格。
 *
 * ★ 写「今天 / 明天」而不是日期数字:调度一眼要判断的是
 *   「这单是不是今天的活」,而 `09-23` 要他自己先想一下今天几号。
 *   过了的那些标红 —— 那正是司机端会亮提醒的那几单。
 */
function ExpectedDay({ at, tz, status }: { at: string; tz: string; status: string }) {
  // ★ 文案只在 lib/format 里写一次 —— 列表和详情各写一套的话,
  //   改了一处忘了另一处,同一单在两个地方显示成两天
  const { text, state } = expectedLabel(at, tz, status);
  /*
   * ★ 「不是今天」一律红字,过期的和明天的同一个颜色。
   *
   *   原来明天是琥珀色、过期是红色 —— 分得很细,但分错了地方:
   *   这一列要回答的是「这一单是不是今天的活」,而琥珀在手机上
   *   扫一眼根本挑不出来(卡片本来就是灰字为主)。
   *   过期和明天的区别由**文字**给("09-20(已过)" vs "明天"),
   *   不靠颜色 —— 颜色只管「要不要停下来看这一单」这一件事。
   */
  // 做完了的单(送达 / 退回 / 取消)不再要人停下来看 —— 和「今天」同一个颜色
  const cls =
    state === 'today' || state === 'done'
      ? 'text-slate-600 dark:text-slate-300'
      : 'text-red-600 dark:text-red-400 font-medium';
  return (
    <span className={cls} title={dayIn(tz, new Date(at))}>
      {text}
    </span>
  );
}
